/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        User Workspace Root Application Component (Task 10.1.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useActiveWorkspaceTemplate } from './hooks/useActiveWorkspaceTemplate';
import { useWorkspaceStore } from './stores/useWorkspaceStore';
import { useUiStore } from './stores/useUiStore';
import { WorkspaceSettingsView } from './views/WorkspaceSettingsView';
import { EditorView } from './views/EditorView';
import { LongformArticleWizard } from './components/wizard/LongformArticleWizard';
import { PromptTemplatesCatalogView } from './views/PromptTemplatesCatalogView';
import { ChatStudioView } from './views/ChatStudioView';
import { KnowledgeBaseView } from './views/KnowledgeBaseView';
import { WidgetStudioView } from './views/WidgetStudioView';
import { ImageStudioView } from './views/ImageStudioView';
import { InpaintingStudioView } from './views/InpaintingStudioView';
import { ImageOpsStudioView } from './views/ImageOpsStudioView';
import { VideoStudioView } from './views/VideoStudioView';
import { TalkingAvatarStudioView } from './views/TalkingAvatarStudioView';
import { MediaGalleryView } from './views/MediaGalleryView';
import { CatTranslationView } from './views/CatTranslationView';
import { VoiceCatalogView } from './views/VoiceCatalogView';
import { AudioMixerStudioView } from './views/AudioMixerStudioView';
import { SubtitleStudioView } from './views/SubtitleStudioView';
import { ApiKeyManagementView } from './views/ApiKeyManagementView';
import { CreditLedgerView } from './views/CreditLedgerView';
import { SubscriptionPlansView } from './views/SubscriptionPlansView';
import { DashboardOverviewView } from './views/DashboardOverviewView';
import { UserAuthView } from './views/UserAuthView';

import { client } from './lib/api-client';
import { NAVIGATION_SECTIONS } from './navigation/navigation-items';
import { useI18nStore } from './stores/useI18nStore';
import { useAuthStore } from './stores/useAuthStore';
import { SophiaIcon } from '@sofia/assets';
import {
  CheckCircle2,
  AlertCircle,
  Layers,
  ArrowRight,
} from 'lucide-react';

/**
 * Normalizes initial URL pathname into standard workspace routes.
 * Aliases '/', '/workspace', '/workspace/', and '/dashboard' all resolve to '/workspace/dashboard'.
 */
function getInitialWorkspacePath(): string {
  if (typeof window === 'undefined') return '/workspace/dashboard';
  const pathname = window.location.pathname;
  if (
    !pathname ||
    pathname === '/' ||
    pathname === '/workspace' ||
    pathname === '/workspace/' ||
    pathname === '/dashboard' ||
    pathname === '/workspace/dashboard'
  ) {
    return '/workspace/dashboard';
  }
  if (pathname === '/login' || pathname === '/register') {
    return pathname;
  }
  return pathname.startsWith('/workspace') ? pathname : '/workspace/dashboard';
}

export const App: React.FC = () => {
  const { t } = useI18nStore();
  const { LayoutComponent } = useActiveWorkspaceTemplate();
  const [currentPath, setCurrentPath] = useState<string>(getInitialWorkspacePath);
  const { activeWorkspaceId, workspaces, setWorkspaces } = useWorkspaceStore();
  const { sidebarExpanded } = useUiStore();

  /**
   * Unified navigation handler synchronizing internal state and browser history.
   */
  const handleNavigate = useCallback((path: string) => {
    const targetPath =
      path === '/' || path === '/workspace' || path === '/workspace/' || path === '/dashboard'
        ? '/workspace/dashboard'
        : path;

    setCurrentPath(targetPath);
    if (typeof window !== 'undefined' && window.location.pathname !== targetPath) {
      window.history.pushState({}, '', targetPath);
    }
  }, []);

  // Listen for browser popstate (back / forward buttons)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handlePopState = () => {
      const pathname = window.location.pathname;
      if (
        !pathname ||
        pathname === '/' ||
        pathname === '/workspace' ||
        pathname === '/workspace/' ||
        pathname === '/dashboard' ||
        pathname === '/workspace/dashboard'
      ) {
        setCurrentPath('/workspace/dashboard');
      } else if (
        pathname.startsWith('/workspace') ||
        pathname === '/login' ||
        pathname === '/register'
      ) {
        setCurrentPath(pathname);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Listen for auth_token, token, or impersonate_token on load (Task 7.1 / Task 11.4.3)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const token = params.get('impersonate_token') || params.get('auth_token') || params.get('token');
    if (token) {
      try {
        const base64Url = token.split('.')[1];
        if (base64Url) {
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(
            atob(base64)
              .split('')
              .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
              .join('')
          );
          const payload = JSON.parse(jsonPayload);
          useAuthStore.getState().setSession(token, {
            id: payload.sub || payload.id || 'usr_authenticated',
            email: payload.email || 'user@sophiaplatform.com',
            name: payload.fullName || payload.name || payload.email?.split('@')[0] || 'User',
            role: payload.role || 'user',
            avatarUrl: payload.avatarUrl || null,
            isImpersonated: Boolean(params.get('impersonate_token')),
            originalAdminId: payload.original_admin_id,
            originalAdminEmail: payload.original_admin_email,
          });
          const newUrl = window.location.pathname.startsWith('/login') || window.location.pathname.startsWith('/register')
            ? '/workspace/dashboard'
            : window.location.pathname + window.location.hash;
          window.history.replaceState({}, document.title, newUrl);
          if (newUrl.startsWith('/workspace')) {
            setCurrentPath(newUrl.split('?')[0].split('#')[0]);
          }
        }
      } catch (err) {
        console.error('[UserWorkspace] Failed to parse auth token:', err);
      }
    }
  }, []);


  const [apiPingStatus, setApiPingStatus] = useState<'checking' | 'connected' | 'disconnected'>('checking');
  const [latencyMs, setLatencyMs] = useState<number | null>(null);

  // Initialize sample workspaces if store is empty
  useEffect(() => {
    if (workspaces.length === 0) {
      setWorkspaces([
        {
          id: 'ws_demo_primary',
          name: 'Sophia Creative Labs',
          slug: 'sophia-creative-labs',
          role: 'OWNER',
          type: 'personal',
        },
        {
          id: 'ws_demo_enterprise',
          name: 'Enterprise Translation Studio',
          slug: 'enterprise-translation-studio',
          role: 'ADMIN',
          type: 'team',
        },
      ]);
    }
  }, [workspaces.length, setWorkspaces]);

  // Test Hono RPC connection to edge api
  useEffect(() => {
    let mounted = true;
    const testConnection = async () => {
      const start = Date.now();
      try {
        const res = await client.api.v1.public.theme.$get();
        if (mounted) {
          if (res.ok) {
            setApiPingStatus('connected');
            setLatencyMs(Date.now() - start);
          } else {
            setApiPingStatus('disconnected');
          }
        }
      } catch (err) {
        if (mounted) {
          console.warn('[App] Edge RPC probe warning:', err);
          setApiPingStatus('disconnected');
        }
      }
    };

    void testConnection();
    return () => {
      mounted = false;
    };
  }, []);

  // Find active item info
  const activeItem = useMemo(() => {
    for (const section of NAVIGATION_SECTIONS) {
      const found = section.items.find((item) => item.path === currentPath);
      if (found) return { section, item: found };
    }
    return {
      section: NAVIGATION_SECTIONS[0],
      item: NAVIGATION_SECTIONS[0].items[0],
    };
  }, [currentPath]);

  const ItemIcon = activeItem.item.icon;

  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Redirect authenticated users away from auth routes to dashboard
  useEffect(() => {
    if (isAuthenticated && (currentPath === '/login' || currentPath === '/register')) {
      handleNavigate('/workspace/dashboard');
    }
  }, [isAuthenticated, currentPath, handleNavigate]);

  if (!isAuthenticated) {
    return (
      <UserAuthView
        initialMode={currentPath === '/register' ? 'register' : 'login'}
        onSuccess={() => {
          handleNavigate('/workspace/dashboard');
        }}
      />
    );
  }

  return (
    <LayoutComponent currentPath={currentPath} onNavigate={handleNavigate}>
      {/* 1. Active Section Hero Banner */}
      <div className="relative overflow-hidden rounded-2xl border border-surface-border bg-surface p-6 sm:p-8 shadow-glass">
        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-accent/10 blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-wider font-semibold text-primary">
                {activeItem.section.title}
              </span>
              {activeItem.item.badge && (
                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-bold bg-accent/15 text-accent border border-accent/20">
                  {activeItem.item.badge}
                </span>
              )}
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
                {activeItem.item.sophiaIcon ? (
                  <SophiaIcon name={activeItem.item.sophiaIcon} size={20} className="w-5 h-5" />
                ) : (
                  <ItemIcon className="w-5 h-5" />
                )}
              </div>
              <h1 className="text-2xl sm:text-3xl font-heading font-extrabold text-foreground tracking-tight">
                {activeItem.item.label}
              </h1>
            </div>

            <p className="text-foreground-muted text-sm max-w-2xl">
              {activeItem.item.description || 'Enterprise Edge-native studio space powered by Sophia Platform.'}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex items-center gap-2 px-3.5 py-2 rounded-xl border border-surface-border bg-surface-elevated text-xs font-mono">
              <Layers className="w-4 h-4 text-primary" />
              <span>Workspace: {activeWorkspaceId ?? 'none'}</span>
            </div>
            <div className="flex items-center gap-2 px-3.5 py-2 rounded-xl border border-surface-border bg-surface-elevated text-xs font-mono">
              {apiPingStatus === 'connected' ? (
                <>
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  <span>RPC: {latencyMs ? `${latencyMs}ms` : 'Ready'}</span>
                </>
              ) : (
                <>
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                  <span>RPC: Standby</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 2. Specific View Router or Navigation Matrix Preview */}
      {currentPath === '/workspace/dashboard' ||
      currentPath === '/workspace' ||
      currentPath === '/' ||
      currentPath === '/dashboard' ||
      currentPath === '/login' ||
      currentPath === '/register' ? (
        <DashboardOverviewView onNavigate={handleNavigate} />
      ) : currentPath === '/workspace/documents' ? (
        <EditorView onOpenWizard={() => handleNavigate('/workspace/wizard')} />
      ) : currentPath === '/workspace/writer' || currentPath === '/workspace/templates' ? (
        <PromptTemplatesCatalogView
          onNavigateToWizard={() => handleNavigate('/workspace/wizard')}
          onNavigateToEditor={() => handleNavigate('/workspace/documents')}
        />
      ) : currentPath === '/workspace/wizard' ? (
        <LongformArticleWizard onNavigateToEditor={() => handleNavigate('/workspace/documents')} />
      ) : currentPath === '/workspace/chat' || currentPath === '/workspace/assistants' ? (
        <ChatStudioView
          onNavigateToTemplates={() => handleNavigate('/workspace/templates')}
          onNavigateToRAG={() => handleNavigate('/workspace/rag')}
        />
      ) : currentPath === '/workspace/rag' || currentPath === '/workspace/knowledge' ? (
        <KnowledgeBaseView />
      ) : currentPath === '/workspace/widgets' ? (
        <WidgetStudioView />
      ) : currentPath === '/workspace/media/images' ? (
        <ImageStudioView
          onNavigateToInpaint={() => handleNavigate('/workspace/media/canvas')}
          onNavigateToOps={() => handleNavigate('/workspace/media/ops')}
        />
      ) : currentPath === '/workspace/media/canvas' ? (
        <InpaintingStudioView onNavigateToGallery={() => handleNavigate('/workspace/gallery')} />
      ) : currentPath === '/workspace/media/ops' ? (
        <ImageOpsStudioView
          onNavigateToInpaint={() => handleNavigate('/workspace/media/canvas')}
          onNavigateToGallery={() => handleNavigate('/workspace/gallery')}
        />
      ) : currentPath === '/workspace/media/video' ? (
        <VideoStudioView
          onNavigateToGallery={() => handleNavigate('/workspace/gallery')}
          onNavigateToAvatar={() => handleNavigate('/workspace/media/avatar')}
        />
      ) : currentPath === '/workspace/media/avatar' ? (
        <TalkingAvatarStudioView
          onNavigateToVideo={() => handleNavigate('/workspace/media/video')}
          onNavigateToGallery={() => handleNavigate('/workspace/gallery')}
        />
      ) : currentPath === '/workspace/gallery' || currentPath === '/workspace/media/gallery' ? (
        <MediaGalleryView
          onNavigateToImage={() => handleNavigate('/workspace/media/images')}
          onNavigateToVideo={() => handleNavigate('/workspace/media/video')}
          onNavigateToAvatar={() => handleNavigate('/workspace/media/avatar')}
        />
      ) : currentPath === '/workspace/translation' || currentPath === '/workspace/translate' || currentPath.startsWith('/workspace/translation/') ? (
        <CatTranslationView />
      ) : currentPath === '/workspace/voice/mixer' || currentPath === '/workspace/voice/dialogue' || currentPath === '/workspace/voice/daw' ? (
        <AudioMixerStudioView />
      ) : currentPath === '/workspace/voice/transcribe' || currentPath === '/workspace/voice/transcription' || currentPath === '/workspace/voice/subtitles' || currentPath === '/workspace/voice/whisper' ? (
        <SubtitleStudioView />
      ) : currentPath === '/workspace/voice/catalog' || currentPath === '/workspace/voice/tts' || currentPath === '/workspace/voice/cloning' || currentPath.startsWith('/workspace/voice') ? (
        <VoiceCatalogView />
      ) : currentPath === '/workspace/developer/keys' || currentPath === '/workspace/api-keys' || currentPath === '/workspace/developer' || currentPath === '/workspace/developer/analytics' ? (
        <ApiKeyManagementView />
      ) : currentPath === '/workspace/billing/wallet' || currentPath === '/workspace/billing/ledger' || currentPath === '/workspace/billing/credits' || currentPath === '/workspace/ledger' ? (
        <CreditLedgerView />
      ) : currentPath === '/workspace/billing/plans' || currentPath === '/workspace/billing/subscription' ? (
        <SubscriptionPlansView initialTab="plans" />
      ) : currentPath === '/workspace/billing/orders' || currentPath === '/workspace/billing/invoices' ? (
        <SubscriptionPlansView initialTab="orders" />
      ) : currentPath === '/workspace/settings' ? (
        <WorkspaceSettingsView />
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-bold text-lg text-foreground">Platform Modules & Studios</h3>
            <span className="text-xs text-foreground-muted">
              Sidebar: {sidebarExpanded ? 'Expanded (260px)' : 'Collapsed (68px)'}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {NAVIGATION_SECTIONS.map((section) => {
              const isCurrentSection = section.id === activeItem.section.id;
              return (
                <div
                  key={section.id}
                  className={`p-4 rounded-xl border transition-all duration-200 bg-surface flex flex-col justify-between space-y-3 ${
                    isCurrentSection
                      ? 'border-primary shadow-glow-primary/20 ring-1 ring-primary/30'
                      : 'border-surface-border hover:border-surface-border/80'
                  }`}
                >
                  <div className="space-y-1.5">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-primary">
                      {section.title}
                    </span>
                    <div className="space-y-1">
                      {section.items.map((subItem) => {
                        const isSubActive = currentPath === subItem.path;
                        const SubIcon = subItem.icon;
                        return (
                          <button
                            key={subItem.id}
                            onClick={() => handleNavigate(subItem.path)}
                            className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs transition-colors ${
                              isSubActive
                                ? 'bg-primary text-primary-fg font-semibold'
                                : 'text-foreground-muted hover:text-foreground hover:bg-muted/60'
                            }`}
                          >
                            <div className="flex items-center gap-2 truncate">
                              <SubIcon className="w-3.5 h-3.5 flex-shrink-0" />
                              <span className="truncate">{subItem.label}</span>
                            </div>
                            {subItem.badge && (
                              <span
                                className={`text-[9px] px-1.5 py-0.2 rounded-full font-bold uppercase ${
                                  isSubActive
                                    ? 'bg-white/20 text-white'
                                    : 'bg-accent/15 text-accent'
                                }`}
                              >
                                {subItem.badge}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 3. Task Status & Next Steps Roadmap */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 rounded-xl border border-primary/20 bg-primary/5 text-xs gap-3">
        <div className="flex items-center gap-2.5">
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse flex-shrink-0" />
          <span className="text-foreground font-medium">
            {t('app.roadmapBanner')}
          </span>
        </div>
        <div className="flex items-center gap-1.5 text-primary font-semibold flex-shrink-0">
          <span>{t('app.nextRoadmap')}</span>
          <ArrowRight className="w-3.5 h-3.5" />
        </div>
      </div>
    </LayoutComponent>
  );
};

